/**
 * Bill / selling zod schemas (docs/SPEC.md §5).
 */
import { z } from 'zod';
import { idSchema, paginationSchema, paisaSchema, phoneSchema, positiveQtyMilliSchema } from './common.js';
import { DISCOUNT_KINDS, PAYMENT_METHODS, PRICE_MODES, SALE_CHANNELS } from '../constants.js';
export const addBillLineSchema = z.object({
    variantId: idSchema,
    batchId: idSchema.nullable().optional(),
    qtyMilli: positiveQtyMilliSchema,
    /** Omit to let the price cascade resolve it; send to override (needs SALES_PRICE_OVERRIDE). */
    unitPricePaisa: paisaSchema.optional(),
    priceMode: z.enum(PRICE_MODES).optional(),
    discountPaisa: paisaSchema.min(0).optional(),
    note: z.string().max(300).optional(),
    barcodeScanned: z.string().max(64).optional(),
    /** Offline-queue replay idempotency (lib/offline-queue.ts) — a client UUID, never server-generated. */
    clientOpId: z.string().max(64).optional(),
});
export const updateBillLineSchema = z.object({
    qtyMilli: positiveQtyMilliSchema.optional(),
    unitPricePaisa: paisaSchema.optional(),
    discountPaisa: paisaSchema.min(0).optional(),
    note: z.string().max(300).optional(),
});
export const createDraftBillSchema = z.object({
    shopId: idSchema,
    channel: z.enum(SALE_CHANNELS).default('COUNTER'),
    customerId: idSchema.nullable().optional(),
    customerPhone: z.string().max(20).optional(),
    customerName: z.string().max(120).optional(),
    priceListId: idSchema.nullable().optional(),
    lines: z.array(addBillLineSchema).default([]),
    /** Offline-queue replay idempotency (lib/offline-queue.ts) — a client UUID, never server-generated. */
    clientOpId: z.string().max(64).optional(),
    /**
     * Cold-start offline sale (README.md Phase 21) — a bill number the client
     * already minted from a `POST /bills/reserve-block` block and already
     * printed on the customer's receipt while offline. Only honoured
     * server-side when `clientOpId` is also present (see bill.service.ts's
     * `createDraft`) — an online create can never set its own number.
     */
    reservedBillNo: z.string().max(32).optional(),
});
export const setBillDiscountSchema = z.object({
    kind: z.enum(DISCOUNT_KINDS),
    /** Percent discounts (bps) resolve to an absolute amount server-side. */
    valueBps: z.number().int().min(0).max(100_000).optional(),
    amountPaisa: paisaSchema.min(0).optional(),
    /** LOYALTY only — points the customer wants to spend; the server, never the client, prices them. */
    points: z.number().int().min(1).optional(),
    reason: z.string().max(300).optional(),
});
export const attachCustomerSchema = z
    .object({
    customerId: idSchema.optional(),
    phone: phoneSchema.optional(),
    name: z.string().max(200).optional(),
})
    .refine((v) => v.customerId || v.phone, { message: 'Provide a customerId or a phone number' });
export const billPaymentSchema = z.object({
    method: z.enum(PAYMENT_METHODS),
    amountPaisa: paisaSchema.min(1),
    reference: z.string().max(120).optional(),
    tenderedPaisa: paisaSchema.min(0).optional(),
    /** Optional screenshot/proof for a bank/wallet transfer — a `StoredFile.id` from `POST /files`. */
    proofFileId: idSchema.optional(),
});
export const payBillSchema = z.object({
    payments: z.array(billPaymentSchema).min(1),
    /** Offline-queue replay idempotency (lib/offline-queue.ts) — a client UUID, never server-generated. */
    clientOpId: z.string().max(64).optional(),
});
/**
 * Reserves a block of bill numbers for cold-start offline selling (README.md
 * Phase 21) — called while the terminal is still online, before it needs
 * them, so a sale started with zero connectivity still gets a real,
 * collision-free number to print immediately.
 */
export const reserveBillBlockSchema = z.object({
    shopId: idSchema,
    size: z.number().int().min(1).max(200).default(20),
});
export const voidBillSchema = z.object({
    reason: z.string().max(300).min(1),
});
export const listBillsQuerySchema = paginationSchema.extend({
    shopId: idSchema.optional(),
    status: z.string().optional(),
    customerId: idSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    search: z.string().max(120).optional(),
});
//# sourceMappingURL=selling.js.map